import { stringifyLosslessJson } from '@anchorbase/pillar-sdk';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { type MerchantPaymentMethodTokenDetailsView } from '@anchorbase/pillar-sdk';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError, paymentsApi } from '@/lib/pillarApi';
import { describePaymentMethod } from '@/lib/payment-methods/describePaymentMethod';

const paymentMethodDisplayDeniedMessage =
    'This payment method is unavailable because you have not been granted the appropriate permission.';

function DetailRow(props: {
    label: string;
    value: string;
}) {
    return (
        <div className="grid gap-1 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-4">
            <div className="text-sm font-medium text-slate-700">{props.label}</div>
            <div className="text-sm text-slate-900 break-all">{props.value}</div>
        </div>
    );
}

export function CardVaultPaymentMethodDetailContent() {
    const params = useParams();
    const paymentMethodTokenUuidParseResult = z.string().uuid().safeParse(params.id);
    if (!paymentMethodTokenUuidParseResult.success) {
        throw new Error(`Invalid payment method token UUID: ${params.id}`);
    }
    const paymentMethodTokenUuid: string = paymentMethodTokenUuidParseResult.data;

    const paymentMethodQuery = useQuery<MerchantPaymentMethodTokenDetailsView>({
        queryKey: ['merchantPaymentMethodTokenDetails', paymentMethodTokenUuid],
        queryFn: async () =>
            await paymentsApi.getMerchantPaymentMethodTokenDetails(paymentMethodTokenUuid),
        retry: false,
    });

    if (paymentMethodQuery.isLoading) {
        return <div className="p-6 text-sm text-muted-foreground">Loading payment method...</div>;
    }

    if (paymentMethodQuery.error !== null) {
        const errorMessage: string =
            paymentMethodQuery.error instanceof ApiError && paymentMethodQuery.error.status === 403
                ? paymentMethodDisplayDeniedMessage
                : paymentMethodQuery.error instanceof ApiError
                  ? paymentMethodQuery.error.message
                  : 'Failed to load payment method.';

        return (
            <div className="p-6">
                <Alert variant="destructive">
                    <AlertTitle>Unable to load payment method</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
            </div>
        );
    }

    const paymentMethod = paymentMethodQuery.data;
    if (paymentMethod === undefined) {
        return null;
    }

    const description = describePaymentMethod(paymentMethod);

    return (
        <div className="space-y-6 p-6">
            <div className="space-y-1">
                <h1 className="text-3xl font-semibold tracking-tight">{description.title}</h1>
                <p className="text-sm text-muted-foreground">
                    {paymentMethod.holder_organization_name}
                </p>
            </div>

            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                    <div className="space-y-1">
                        <CardTitle>Payment Method Details</CardTitle>
                        <CardDescription>{description.subtitle}</CardDescription>
                    </div>
                    {paymentMethod.can_delete ? (
                        <Button type="button" variant="destructive">
                            Delete
                        </Button>
                    ) : null}
                </CardHeader>
                <CardContent className="space-y-4">
                    <DetailRow
                        label="Payment Method UUID"
                        value={paymentMethod.payment_method_token_uuid}
                    />
                    <DetailRow label="Payment Rail" value={paymentMethod.payment_rail} />
                    <DetailRow label="Gateway ID" value={String(paymentMethod.gateway_id)} />
                    <DetailRow label="Gateway Token" value={paymentMethod.gateway_token} />
                    <DetailRow
                        label="Stored for Future Use"
                        value={paymentMethod.stored_for_future_use ? 'Yes' : 'No'}
                    />
                    <DetailRow label="Created" value={paymentMethod.created_ts} />
                    {paymentMethod.created_by_transaction_uuid !== undefined ? (
                        <DetailRow
                            label="Created by Transaction"
                            value={paymentMethod.created_by_transaction_uuid}
                        />
                    ) : null}
                    <DetailRow
                        label="Token Details"
                        value={stringifyLosslessJson(paymentMethod.payment_method_token_details, null, 2)}
                    />
                    {paymentMethod.customer_details !== undefined ? (
                        <DetailRow
                            label="Customer Details"
                            value={stringifyLosslessJson(paymentMethod.customer_details, null, 2)}
                        />
                    ) : null}
                </CardContent>
            </Card>
        </div>
    );
}
